import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  FiEye,
  FiEyeOff,
  FiUserPlus,
  FiArrowLeft,
  FiArrowRight,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { LEGAL_URLS } from '../lib/supabase';
import { USER_ROLES } from '../types/user';
import { translateRole } from '../utils/translations';
import {
  Button,
  TextField,
  Select,
  Alert,
  Card,
  CardContent,
  Stepper,
  Checkbox,
} from './ds';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: USER_ROLES.USER,
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'España',
    },
    acceptTerms: false,
    acceptPrivacy: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const steps = ['Información Personal', 'Cuenta', 'Dirección', 'Finalizar'];

  const roleOptions = [
    { value: USER_ROLES.USER, label: translateRole(USER_ROLES.USER) },
    { value: USER_ROLES.CHEF, label: translateRole(USER_ROLES.CHEF) },
    { value: USER_ROLES.CLIENT, label: translateRole(USER_ROLES.CLIENT) },
  ];

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.firstName) newErrors.firstName = 'El nombre es requerido';
        if (!formData.lastName) newErrors.lastName = 'El apellido es requerido';
        if (!formData.username) newErrors.username = 'El nombre de usuario es requerido';
        if (!formData.email) newErrors.email = 'El email es requerido';

        {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (formData.email && !emailRegex.test(formData.email)) {
            newErrors.email = 'Email inválido';
          }
        }

        {
          const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
          if (formData.username && !usernameRegex.test(formData.username)) {
            newErrors.username = 'El usuario debe tener entre 3-20 caracteres alfanuméricos';
          }
        }
        break;

      case 1:
        if (!formData.password) newErrors.password = 'La contraseña es requerida';
        if (formData.password.length < 6) {
          newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        }
        if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirma tu contraseña';
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }
        break;

      case 2:
        break;

      case 3:
        if (!formData.acceptTerms) {
          newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
        }
        if (!formData.acceptPrivacy) {
          newErrors.acceptPrivacy = 'Debes aceptar la política de privacidad';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    setLoading(true);

    try {
      const result = await register(formData);

      if (result.success) {
        navigate('/');
      } else {
        setErrors({ submit: result.error || 'Error al crear la cuenta' });
      }
    } catch (error) {
      setErrors({ submit: 'Error de conexión. Inténtalo de nuevo.' });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="ds-form-grid">
            <TextField
              label="Nombre"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              error={errors.firstName}
              required
            />
            <TextField
              label="Apellido"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              error={errors.lastName}
              required
            />
            <TextField
              className="ds-form-grid--full"
              label="Nombre de Usuario"
              value={formData.username}
              onChange={(e) => handleChange('username', e.target.value)}
              error={errors.username}
              required
            />
            <TextField
              className="ds-form-grid--full"
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              required
            />
            <TextField
              className="ds-form-grid--full"
              label="Teléfono (opcional)"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </div>
        );

      case 1:
        return (
          <div className="ds-form-grid">
            <div className="ds-form-grid--full" style={{ position: 'relative' }}>
              <TextField
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                error={errors.password}
                required
              />
              <button
                type="button"
                className="ds-icon-btn"
                style={{ position: 'absolute', right: 8, top: 28 }}
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Mostrar contraseña"
              >
                {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
              </button>
            </div>
            <div className="ds-form-grid--full" style={{ position: 'relative' }}>
              <TextField
                label="Confirmar Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                error={errors.confirmPassword}
                required
              />
              <button
                type="button"
                className="ds-icon-btn"
                style={{ position: 'absolute', right: 8, top: 28 }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label="Mostrar confirmación de contraseña"
              >
                {showConfirmPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
              </button>
            </div>
            <Select
              className="ds-form-grid--full"
              label="Tipo de Usuario"
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              options={roleOptions}
            />
          </div>
        );

      case 2:
        return (
          <div className="ds-form-grid">
            <TextField
              className="ds-form-grid--full"
              label="Dirección (opcional)"
              value={formData.address.street}
              onChange={(e) => handleChange('address.street', e.target.value)}
            />
            <TextField
              label="Ciudad"
              value={formData.address.city}
              onChange={(e) => handleChange('address.city', e.target.value)}
            />
            <TextField
              label="Provincia/Estado"
              value={formData.address.state}
              onChange={(e) => handleChange('address.state', e.target.value)}
            />
            <TextField
              label="Código Postal"
              value={formData.address.zipCode}
              onChange={(e) => handleChange('address.zipCode', e.target.value)}
            />
            <TextField
              label="País"
              value={formData.address.country}
              onChange={(e) => handleChange('address.country', e.target.value)}
            />
          </div>
        );

      case 3:
        return (
          <div>
            <p className="ds-subsection-title">Términos y Condiciones</p>
            <div className="ds-check-group">
              <Checkbox
                checked={formData.acceptTerms}
                onChange={(e) => handleChange('acceptTerms', e.target.checked)}
                label={
                  <>
                    Acepto los{' '}
                    <a
                      className="ds-link"
                      href={LEGAL_URLS.terms}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      términos y condiciones
                    </a>
                  </>
                }
              />
              {errors.acceptTerms ? (
                <p className="ds-field__error" role="alert">
                  {errors.acceptTerms}
                </p>
              ) : null}

              <Checkbox
                checked={formData.acceptPrivacy}
                onChange={(e) => handleChange('acceptPrivacy', e.target.checked)}
                label={
                  <>
                    Acepto la{' '}
                    <a
                      className="ds-link"
                      href={LEGAL_URLS.privacy}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      política de privacidad
                    </a>
                  </>
                }
              />
              {errors.acceptPrivacy ? (
                <p className="ds-field__error" role="alert">
                  {errors.acceptPrivacy}
                </p>
              ) : null}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="ds-auth-page">
      <Card className="ds-auth-card">
        <CardContent>
          <div className="ds-text-center" style={{ marginBottom: 'var(--space-5)' }}>
            <h1 className="ds-section-title" style={{ marginBottom: 'var(--space-2)' }}>
              Crear Cuenta
            </h1>
            <p className="ds-text-muted">Completa el formulario para crear tu cuenta</p>
          </div>

          <Stepper steps={steps} activeStep={activeStep} />

          {errors.submit ? (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Alert tone="error">{errors.submit}</Alert>
            </div>
          ) : null}

          <div style={{ marginTop: 'var(--space-4)' }}>{renderStepContent(activeStep)}</div>

          <div className="ds-register-actions">
            <Button variant="outline" onClick={handleBack} disabled={activeStep === 0}>
              <FiArrowLeft /> Anterior
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button variant="solid" onClick={handleSubmit} disabled={loading}>
                <FiUserPlus /> {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
              </Button>
            ) : (
              <Button variant="solid" onClick={handleNext}>
                Siguiente <FiArrowRight />
              </Button>
            )}
          </div>

          <div className="ds-text-center" style={{ marginTop: 'var(--space-4)' }}>
            <p className="ds-text-muted">
              ¿Ya tienes una cuenta?{' '}
              <RouterLink className="ds-link" to="/login">
                Iniciar Sesión
              </RouterLink>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
